import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormBuilder, FormGroup } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { saveAs } from 'file-saver';
import { Subject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';

import { AppError } from 'src/app/core/models/api-error';
import { ErrorMessageService } from 'src/app/core/services/error-message.service';
import { copyTextToClipboard } from 'src/app/shared/utils/clipboard.util';
import { secondsUntilOdooDateTime } from 'src/app/shared/utils/helpers';

import { MfaRecoveryState } from '../../../models/mfa.models';
import { MfaService } from '../../../services/mfa.service';
import {
  formatRecoveryCodesFile,
  recoveryCodesFilename,
} from './recovery-codes.format';

/**
 * Recovery codes: issue a batch, show it once, report how many are left.
 *
 * The batch lives here and nowhere else -- not in NgRx, not in storage -- for
 * the same reason the TOTP seed does not: a global store would leave it in the
 * devtools timeline for the rest of the session. Navigating away loses it, and
 * the remedy is another batch rather than keeping this one somewhere.
 */
@Component({
  selector: 'app-recovery-codes-panel',
  templateUrl: './recovery-codes-panel.component.html',
  styleUrls: ['./recovery-codes-panel.component.scss'],
})
export class RecoveryCodesPanelComponent implements OnInit, OnDestroy {
  /** At or below this many codes left, the panel warns. */
  static readonly LOW_THRESHOLD = 3;

  @Input() state: MfaRecoveryState | null = null;
  /** True while the parent is mid-request; keeps the buttons quiet. */
  @Input() disabled = false;
  /** An email factor is active, so the step-up code can be mailed. Without it
   *  an email-OTP-only user can never mint a recovery sheet. */
  @Input() canSendEmailCode = false;

  /** Fires so the parent re-reads the count; the codes are never emitted. */
  @Output() regenerated = new EventEmitter<void>();

  /** The whole secret: one field, cleared on destroy. */
  codes: string[] | null = null;
  acknowledged = false;
  copied = false;
  error: AppError | null = null;
  loading = false;

  /** Shown while collecting the step-up code the server demands. */
  requesting = false;
  stepUpForm!: FormGroup;
  /** A step-up code is being mailed. */
  sendingCode = false;
  /** Server-side throttle on the send, echoed back so the button can wait. */
  codeNextAllowedAt: string | null = null;

  private readonly destroy$ = new Subject<void>();
  private copyTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly fb: FormBuilder,
    private readonly mfa: MfaService,
    private readonly translate: TranslateService,
    private readonly errorMessages: ErrorMessageService
  ) {}

  ngOnInit(): void {
    this.stepUpForm = this.fb.group({ code: [''] });
  }

  ngOnDestroy(): void {
    // The batch must not outlive the view that showed it.
    this.codes = null;
    if (this.copyTimer) {
      clearTimeout(this.copyTimer);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  // --- reading the current state ------------------------------------------

  get remaining(): number {
    return this.state?.remaining ?? 0;
  }

  /** The batch to render, or null. An empty array is truthy in a template,
   *  which rendered the "save these now" panel around an empty list. */
  get batch(): string[] | null {
    return this.codes?.length ? this.codes : null;
  }

  /** Seconds until another step-up code may be sent; 0 when one may be now. */
  get codeResendWait(): number {
    return secondsUntilOdooDateTime(this.codeNextAllowedAt);
  }

  /** Never issued -- distinct from having spent them all. */
  get neverGenerated(): boolean {
    return !this.state?.generated && !this.state?.generated_at;
  }

  get exhausted(): boolean {
    return !!this.state?.generated_at && this.remaining === 0;
  }

  get low(): boolean {
    return (
      this.remaining > 0 &&
      this.remaining <= RecoveryCodesPanelComponent.LOW_THRESHOLD
    );
  }

  // --- issuing -------------------------------------------------------------

  /** Open the step-up form. Required even for a first batch: the server
   *  refuses an access token on its own. */
  startGenerate(): void {
    this.error = null;
    this.requesting = true;
    this.stepUpForm.reset({ code: '' });
  }

  cancelGenerate(): void {
    this.requesting = false;
    this.stepUpForm.reset({ code: '' });
  }

  /** Mail a step-up code -- same purpose as an enrolment resend, which is
   *  what the server checks a step-up against. */
  sendStepUpCode(): void {
    if (this.sendingCode || this.codeResendWait > 0) {
      return;
    }
    this.error = null;
    this.sendingCode = true;
    this.mfa
      .sendStepUpCode()
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.sendingCode = false))
      )
      .subscribe({
        next: (response) =>
          (this.codeNextAllowedAt = response.object?.next_allowed_at ?? null),
        error: (error) => (this.error = this.errorMessages.parse(error as never)),
      });
  }

  confirmGenerate(): void {
    const code = (this.stepUpForm.controls['code'].value as string) || '';
    this.error = null;
    this.loading = true;
    this.mfa
      .generateRecoveryCodes(code || undefined)
      .pipe(
        takeUntil(this.destroy$),
        finalize(() => (this.loading = false))
      )
      .subscribe({
        next: (response) => {
          this.codes = response.object?.codes ?? null;
          this.acknowledged = false;
          this.copied = false;
          this.cancelGenerate();
          // The parent re-reads the count rather than being handed anything
          // derived from the codes.
          this.regenerated.emit();
        },
        error: (error) => (this.error = this.errorMessages.parse(error as never)),
      });
  }

  /** The only way to dismiss the batch, and it takes a deliberate click. */
  dismiss(): void {
    this.codes = null;
    this.acknowledged = true;
    this.copied = false;
  }

  // --- taking the codes away ----------------------------------------------

  get codesText(): string {
    return formatRecoveryCodesFile(this.codes ?? [], {
      header: this.translate.instant('settings_security.recovery.file_header'),
      generatedAt: this.translate.instant('settings_security.recovery.file_generated', {
        date: new Date().toLocaleString(),
      }),
    });
  }

  copyCodes(): void {
    if (!this.codes) {
      return;
    }
    void copyTextToClipboard(this.codes.join('\n')).then((ok) => {
      this.copied = ok;
      if (!ok) {
        return;
      }
      if (this.copyTimer) {
        clearTimeout(this.copyTimer);
      }
      this.copyTimer = setTimeout(() => (this.copied = false), 2000);
    });
  }

  downloadCodes(): void {
    if (!this.codes) {
      return;
    }
    saveAs(
      new Blob([this.codesText], { type: 'text/plain;charset=utf-8' }),
      recoveryCodesFilename(new Date())
    );
  }

  /**
   * Warn before a reload or tab close discards an unsaved batch.
   *
   * In-app navigation is not guarded: there is no CanDeactivate anywhere in
   * this app, and losing a batch is recoverable by issuing another.
   */
  @HostListener('window:beforeunload', ['$event'])
  warnOnUnload(event: BeforeUnloadEvent): boolean | void {
    if (this.codes && !this.acknowledged) {
      event.preventDefault();
      event.returnValue = true;
      return true;
    }
  }

  // ui-form-field preventDefaults Enter, so this form needs its own bridge.
  onEnterGenerate(event: Event): void {
    if ((event.target as HTMLElement | null)?.tagName === 'INPUT') {
      this.confirmGenerate();
    }
  }
}
